<template>
    <form-wrapper class="intake-symptoms-today" @submit="handleFormSubmission(data)">
        <p v-if="!data.problems.length" class="intake-problems__empty">
            {{ $t('forms.intake.symptoms_today.empty') }}
        </p>

        <div v-for="(problem, index) in data.problems" :key="`problem-${index}`" class="intake-problems__card">
            <div class="intake-problems__card-header">
                <h4 class="intake-problems__card-title">
                    {{ $t('forms.intake.symptoms_today.problem_label', { number: index + 1 }) }}
                </h4>
                <common-button
                    :label="$t('common.microcopy.remove')"
                    type="secondary"
                    size="small"
                    :isSubmittable="false"
                    @click="removeProblem(index)"
                ></common-button>
            </div>

            <form-row>
                <form-cell width="half">
                    <select-field
                        :label="$t('forms.intake.symptoms_today.fields.body_region.label')"
                        :placeholder="$t('forms.intake.symptoms_today.fields.body_region.placeholder')"
                        :isRequired="true"
                        :options="bodyRegionOptions"
                        :formData="data"
                        :valuePath="`problems.${index}.bodyRegion`"
                        @change="change($event)"
                    ></select-field>
                </form-cell>
                <form-cell width="half">
                    <select-field
                        :label="$t('forms.intake.symptoms_today.fields.side.label')"
                        :placeholder="$t('forms.intake.symptoms_today.fields.side.placeholder')"
                        :options="sideOptions"
                        :formData="data"
                        :valuePath="`problems.${index}.side`"
                        @change="change($event)"
                    ></select-field>
                </form-cell>
            </form-row>

            <div class="intake-problems__types">
                <label class="form-field__label">
                    {{ $t('forms.intake.symptoms_today.fields.problem_types.label') }}
                    <span class="intake-problems__required">*</span>
                </label>
                <div class="intake-problems__types-grid">
                    <checkbox-input
                        v-for="option in problemTypeOptions"
                        :key="option.value"
                        :label="option.label"
                        :value="isProblemTypeChecked(index, option.value)"
                        @change="toggleProblemType(index, option.value)"
                    ></checkbox-input>
                </div>
            </div>

            <form-row>
                <form-cell>
                    <checkbox-field :formData="data" :valuePath="`problems.${index}.seeingDoctorToday`" @change="change($event)">
                        <template #label>
                            <span>{{ $t('forms.intake.symptoms_today.fields.seeing_doctor_today.label') }}</span>
                        </template>
                    </checkbox-field>
                </form-cell>
            </form-row>
        </div>

        <button class="intake-problems__add" type="button" @click="addProblem">
            + {{ $t('forms.intake.symptoms_today.actions.add_problem') }}
        </button>

        <template #footer>
            <common-buttons>
                <common-button
                    :label="$t('common.microcopy.back')"
                    type="secondary"
                    size="large"
                    :isSubmittable="false"
                    @click="goBack"
                ></common-button>
                <common-button
                    :label="$t('common.microcopy.next')"
                    type="primary"
                    size="large"
                    :isProcessing="isSending"
                ></common-button>
            </common-buttons>
        </template>
    </form-wrapper>
</template>

<script>
    import FormMixin from 'mixins/FormMixin'

    const BODY_REGIONS = [
        'head',
        'neck',
        'upper_back',
        'mid_back',
        'low_back',
        'right_shoulder',
        'left_shoulder',
        'right_arm',
        'left_arm',
        'right_elbow',
        'left_elbow',
        'right_wrist_hand',
        'left_wrist_hand',
        'chest',
        'abdomen',
        'right_hip',
        'left_hip',
        'right_thigh',
        'left_thigh',
        'right_knee',
        'left_knee',
        'right_leg',
        'left_leg',
        'right_ankle_foot',
        'left_ankle_foot',
        'other',
    ]

    const PROBLEM_TYPES = [
        'pain',
        'stiffness',
        'spasm',
        'numbness',
        'tingling',
        'weakness',
        'swelling',
        'instability',
        'limited_motion',
        'other',
    ]

    export default {
        mixins: [FormMixin],

        props: {
            initialData: { type: Object, default: undefined },
        },

        data() {
            return {
                data: {
                    problems: [],
                    ...(this.initialData || {}),
                },
            }
        },

        computed: {
            bodyRegionOptions() {
                return BODY_REGIONS.map((value) => ({
                    label: this.$t(`forms.intake.how_you_felt.fields.body_region.options.${value}`),
                    value,
                }))
            },

            sideOptions() {
                return ['left', 'right', 'bilateral', 'central', 'not_applicable'].map((value) => ({
                    label: this.$t(`forms.intake.how_you_felt.fields.side.options.${value}`),
                    value,
                }))
            },

            problemTypeOptions() {
                return PROBLEM_TYPES.map((value) => ({
                    label: this.$t(`forms.intake.symptoms_today.fields.problem_types.options.${value}`),
                    value,
                }))
            },
        },

        methods: {
            createProblem() {
                return {
                    bodyRegion: undefined,
                    side: undefined,
                    problemTypes: [],
                    seeingDoctorToday: true,
                }
            },

            addProblem() {
                this.change({
                    path: 'problems',
                    value: [...this.data.problems, this.createProblem()],
                })
            },

            removeProblem(index) {
                const next = [...this.data.problems]
                next.splice(index, 1)
                this.change({ path: 'problems', value: next })
            },

            isProblemTypeChecked(index, type) {
                const types = this.data.problems[index]?.problemTypes || []
                return types.includes(type)
            },

            toggleProblemType(index, type) {
                const problem = this.data.problems[index]
                const current = [...(problem.problemTypes || [])]
                const nextTypes = current.includes(type) ? current.filter((item) => item !== type) : [...current, type]

                this.change({
                    path: `problems.${index}.problemTypes`,
                    value: nextTypes,
                })
            },

            goBack() {
                this.emitEvent({ name: 'back', payload: this.data })
            },

            handleFormSubmission(data) {
                this.isSending = true
                this.emitSent(data)
                this.isSending = false
            },
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_functions.scss';
    @import 'styles/setup/_mixins.scss';

    .intake-problems__empty {
        margin: 0 0 rem(12px);
        font-size: rem($font-size--form);
        color: $color--txt-minor;
    }

    .intake-problems__card {
        margin-bottom: rem(16px);
        padding: rem(16px);
        border: rem(1px) solid $color--border-base;
        border-radius: rem($border-radius--base);
        background: #fff;

        @include breakpoint(tablet) {
            padding: rem(12px);
        }
    }

    .intake-problems__card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: rem(12px);
        margin-bottom: rem(12px);
        flex-wrap: wrap;
    }

    .intake-problems__card-title {
        margin: 0;
        font-size: rem($font-size--form);
        font-weight: 600;
        color: $color--txt-header;
    }

    .intake-problems__types {
        margin: rem(20px) 0;
    }

    .intake-problems__required {
        color: $color--accent-4;
        margin-left: rem(2px);
    }

    .intake-problems__types-grid {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: rem(12px) rem(16px);
        margin-top: rem(10px);

        @include breakpoint(tablet) {
            grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        @include breakpoint(smartphone) {
            grid-template-columns: 1fr;
        }
    }

    .intake-problems__add {
        display: block;
        width: 100%;
        margin-top: rem(4px);
        padding: rem(14px) rem(12px);
        border: rem(1px) dashed $color--accent-1;
        border-radius: rem($border-radius--base);
        background: transparent;
        color: $color--accent-1;
        font-size: rem($font-size--form);
        font-weight: 600;
        text-align: center;
        cursor: pointer;

        &:hover {
            background: rgba($color--accent-1, 0.06);
        }
    }
</style>
